import { useEffect, useMemo, useState } from "react";
import { useFormikContext } from "formik";
import * as Yup from "yup";
import {
  Divider,
  FormControl,
  FormControlLabel,
  FormHelperText,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Switch,
  Typography,
} from "@mui/material";
import { ContentState, convertFromHTML, EditorState } from "draft-js";
import { convertToHTML } from "draft-convert";
import useOrganizations from "@/features/organizations/useOrganizations";
import WysiwygEditor from "@/components/WysiwygEditor";
import InputText from "@/components/InputText";
import { useAppSelector } from "@/hooks";
import { Content, Setting } from "@/types";

export const DetailsSchema = Yup.object().shape({
  title: Yup.string().strict(true).min(4, "Muy Corto!").max(50, "Muy Largo!").required("Campo requerido"),
  previewDescription: Yup.string().min(4, "Muy Corto!").max(100, "Muy Largo!"),
  description: Yup.string().min(4, "Muy Corto!"),
  price: Yup.number().strict(true).required("Campo requerido"),
  startingPrice: Yup.number(),
  status: Yup.string().strict(true).required("Campo requerido"),
});

const Details = () => {
  const { currentOrganization } = useOrganizations();
  const { values, handleChange, errors, touched, handleBlur, setFieldValue } = useFormikContext<Content>();
  const activeBranch = useAppSelector((state) => state.ui.activeBranch);

  const [editorState, setEditorState] = useState<EditorState>();

  const isNewContent = useMemo(() => !values?.id, [values?.id]);

  const optionPrices = useMemo(() => {
    const opt = ((values?.settings as Setting[]) ?? []).map((s) => s?.options?.map((o) => (o.type === "option" ? o.price : null)).filter((price) => price !== null));
    return opt;
  }, [values?.settings]);

  const areAllPricesZero = optionPrices.every((p) => {
    return p?.every((price) => price === 0);
  });

  const onEditorStateChange = (e: EditorState) => {
    const html = convertToHTML(e.getCurrentContent());
    setFieldValue("description", html);
    setEditorState(e);
  };

  useEffect(() => {
    if (values?.description && !editorState) {
      const blocksFromHTML = convertFromHTML(values.description);
      const state = ContentState.createFromBlockArray(blocksFromHTML.contentBlocks, blocksFromHTML.entityMap);

      setEditorState(EditorState.createWithContent(state));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [values?.description, editorState]);

  return (
    <>
      <Stack gap={2}>
        <Paper
          elevation={2}
          sx={{
            padding: 2,
            borderRadius: 2,
          }}
        >
          <Stack spacing={2}>
            <InputText label="Nombre" name="title" required={true} />
            <InputText label="Descripción previa" name="previewDescription" rows={2} />
            <Stack>
              <InputLabel error={!!errors.description && !!touched.description} shrink>
                Descripción
              </InputLabel>
              <WysiwygEditor value={editorState ?? EditorState.createEmpty()} onChange={onEditorStateChange} />
            </Stack>
          </Stack>
        </Paper>
      </Stack>

      <Paper sx={{ padding: 2 }} elevation={2}>
        <Stack gap={2}>
          <Typography variant="h4">Configuraciones</Typography>

          <Stack direction={"row"} spacing={3}>
            <InputText label="Precio" name="price" required={true} type="number" />
            {!areAllPricesZero && !isNewContent ? (
              <InputText
                label="Precio Inicial"
                name="startingPrice"
                type="number"
                value={values?.startingPrice ?? ""}
                required={true}
                helperText={"Se recomienda tener el mismo precio que la opción seleccionada"}
              />
            ) : null}
          </Stack>
        </Stack>
      </Paper>

      <Paper sx={{ padding: 2 }} elevation={2}>
        <Stack gap={2}>
          <Typography variant="h4">Estado</Typography>
          <Stack spacing={2}>
            <FormControl fullWidth>
              <InputLabel id="status-select-label">Estado de Publicación</InputLabel>
              <Select
                labelId="status-select-label"
                id="status-select"
                name="status"
                value={values?.status || ""}
                label="Estado de Publicación"
                onChange={handleChange}
                onBlur={handleBlur}
                error={!!errors.status && !!touched.status}
              >
                <MenuItem value="draft">Borrador</MenuItem>
                <MenuItem value="published">Publicado</MenuItem>
                <MenuItem value="archived">Archivado</MenuItem>
              </Select>
              {!!errors.status && !!touched.status && (
                <FormHelperText error>{errors.status}</FormHelperText>
              )}
            </FormControl>
            <Divider />

            {activeBranch ? (
              <FormControlLabel
                label="¿Deseas ocultar plato del menú?"
                labelPlacement="start"
                sx={{
                  margin: 0,
                  display: "flex",
                  justifyContent: "space-between",
                }}
                control={
                  <Switch
                    name={`branchSettings.${activeBranch}.isDisabled`}
                    value={values?.branchSettings?.[activeBranch]?.isDisabled}
                    checked={Boolean(values?.branchSettings?.[activeBranch]?.isDisabled)}
                    onChange={(e) => {
                      setFieldValue(`branchSettings.${activeBranch}.isDisabled`, e.target.checked);
                    }}
                  />
                }
              />
            ) : (
              <FormControlLabel
                label="¿Deseas ocultar plato del menú?"
                labelPlacement="start"
                sx={{
                  margin: 0,
                  display: "flex",
                  justifyContent: "space-between",
                }}
                control={
                  <Switch
                    name="isDisabled"
                    value="isDisabled"
                    checked={Boolean(values?.isDisabled)}
                    onChange={(e) => {
                      setFieldValue("isDisabled", e.target.checked);
                    }}
                  />
                }
              />
            )}

          </Stack>
        </Stack>
      </Paper>
    </>
  );
};

export default Details;
